/** What an extractor takes in now, as a speedometer: its dial runs from nothing to its most, the needle at its rate. */
import type { JSX } from 'react';

import { numberText, t } from '@i18n/index';

/** The dial: centre, radius, and the arc from lower left over the top to lower right. */
const CX = 60;
const CY = 58;
const R = 44;
const START = 160;
const SWEEP = 220;

const toXY = (deg: number, r = R): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
};

/** An arc of the dial from 0 to `share` of it. */
function arc(share: number): string {
  const end = START + SWEEP * Math.max(0, Math.min(1, share));
  const [x0, y0] = toXY(START);
  const [x1, y1] = toXY(end);
  const large = end - START > 180 ? 1 : 0;
  return `M${x0.toFixed(2)} ${y0.toFixed(2)}A${String(R)} ${String(R)} 0 ${String(large)} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`;
}

export function IntakeGauge({
  rate,
  most,
  period,
}: {
  rate: number;
  most: number;
  period: string;
}): JSX.Element {
  const share = most > 0 ? Math.min(1, rate / most) : 0;
  const needle = toXY(START + SWEEP * share, R - 13);
  // a tick per unit, while they fit
  const ticks = most > 0 && most <= 12 ? Array.from({ length: most + 1 }, (_, k) => k / most) : [];
  return (
    <div className="intake" data-stat="intake">
      <span className="k">{t('info.intake')}</span>
      <svg className="dial" viewBox="0 0 120 92" aria-hidden="true">
        <defs>
          <linearGradient id="intake-arc" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="#5fe0c8" />
            <stop offset="1" className="tone-stop" />
          </linearGradient>
        </defs>
        <path className="track" d={arc(1)} />
        {share > 0 && <path className="value" d={arc(share)} />}
        {ticks.map((at) => {
          const [x0, y0] = toXY(START + SWEEP * at, R - 7);
          const [x1, y1] = toXY(START + SWEEP * at, R - 3);
          return <line key={at} className="tick" x1={x0} y1={y0} x2={x1} y2={y1} />;
        })}
        <line className="needle" x1={CX} y1={CY} x2={needle[0]} y2={needle[1]} />
        <circle className="hub" cx={CX} cy={CY} r={4.5} />
      </svg>
      <b>
        {numberText(rate)}
        <small>/{most}</small>
      </b>
      <small>{t('info.per', { period })}</small>
    </div>
  );
}
